import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Paper, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, styled, Grid, Chip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
// GridLegacy: MUI v7 cambió el Grid por defecto a una API nueva (prop `size`) que ya NO
// entiende `item`/`xs`/`md` -- esas props se ignoran y los campos quedan sin ancho asignado
// (se ve como cajas chicas con mucho espacio en blanco). GridLegacy sí soporta la sintaxis
// clásica tal cual está escrita en el formulario de "Editar/Agregar Usuario". Solo se usa
// ahí (alias FormGrid) -- el resto del archivo (barra de filtros) sigue con el Grid nuevo.
import { GridLegacy as FormGrid } from '@mui/material';
import { Refresh as RefreshIcon, Edit as EditIcon, Delete as DeleteIcon, Person as PersonIcon, Group as GroupIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../config/axiosConfig';
import { FIELD_LIMITS } from '../../constants/fieldLimits';

const StyledTableRow = styled(TableRow)({
  '&.selected': {
    backgroundColor: '#ADD8E6 !important',
    '&:hover': {
      backgroundColor: '#ADD8E6 !important'
    }
  }
});

const initialUser = {
  username: '',
  email: '',
  passwordHash: '',
  firstName: '',
  firstLastName: '',
  secondLastName: '',
  roleId: null,
  isActive: true
};

const CatalogoUsuarios = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [companyByEmail, setCompanyByEmail] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // Error para la pantalla principal
  const [dialogError, setDialogError] = useState(null); // Error específico del diálogo
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUser, setEditUser] = useState(initialUser);
  const [filter, setFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [roles, setRoles] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const resetForm = () => {
    setEditUser(initialUser);
    setFieldErrors({});
    setDialogError(null); // Limpiar error del diálogo
  };

  // Función para validar username en tiempo real
  const handleUsernameChange = async (e) => {
    const newUsername = e.target.value;
    setEditUser({ ...editUser, username: newUsername });
    
    // Limpiar error previo
    setFieldErrors(prev => ({ ...prev, username: '' }));
    
    if (newUsername && newUsername.length >= 3 && !isEditMode) {
      try {
        const response = await api.get(`/users/check-username/${newUsername}`);
        if (response.data.exists) {
          setFieldErrors(prev => ({ 
            ...prev, 
            username: `El username "${newUsername}" ya está en uso` 
          }));
        }
      } catch (err) {
        // Si el endpoint no existe, continuar sin validación
        console.log('Validación de username no disponible');
      }
    }
  };

  // Función para validar email en tiempo real
  const handleEmailChange = async (e) => {
    const newEmail = e.target.value;
    setEditUser({ ...editUser, email: newEmail });
    
    // Limpiar error previo
    setFieldErrors(prev => ({ ...prev, email: '' }));
    
    // Validar formato
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (newEmail && !emailRegex.test(newEmail)) {
      setFieldErrors(prev => ({ ...prev, email: 'Formato de email inválido' }));
      return;
    }
    
    if (newEmail && emailRegex.test(newEmail) && !isEditMode) {
      try {
        const response = await api.get(`/users/check-email/${newEmail}`);
        if (response.data.exists) {
          setFieldErrors(prev => ({ 
            ...prev, 
            email: `El email "${newEmail}" ya está registrado` 
          }));
        }
      } catch (err) {
        // Si el endpoint no existe, continuar sin validación
        console.log('Validación de email no disponible');
      }
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/users');
      console.log('🔍 Usuarios recibidos del backend:', response.data);
      
      // Diagnosticar usuarios con rol "Ejecutivo de Operaciones"
      const ejecutivosOperaciones = response.data.filter(user => 
        user.role === 'EJECUTIVO_OPERACIONES' || 
        user.role === 'Ejecutivo de Operaciones' ||
        user.roleId === 4
      );
      console.log('👨‍💼 Usuarios Ejecutivo de Operaciones encontrados:', ejecutivosOperaciones);
      
      // Diagnosticar usuarios que se muestran como "INVITADO"
      const invitados = response.data.filter(user => user.role === 'INVITADO');
      console.log('👤 Usuarios mostrados como INVITADO:', invitados);
      
      setUsers(response.data);
    } catch (err) {
      setError('Error al cargar los usuarios');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Un invitado se vincula a su empresa por corporate_contact_email == users.email
  // (no existe un company_id en la tabla users), igual que en /users/me.
  const fetchCompaniesForInvitados = async () => {
    try {
      const response = await api.get('/companies');
      const map = {};
      (response.data || []).forEach(company => {
        if (company.corporateContactEmail) {
          map[company.corporateContactEmail.toLowerCase()] = company.businessName;
        }
      });
      setCompanyByEmail(map);
    } catch (err) {
      console.error('Error al cargar empresas para invitados:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchCompaniesForInvitados();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles');
      console.log('🎭 Roles obtenidos del backend:', response.data);
      setRoles(response.data);
    } catch (err) {
      console.error('Error al cargar roles:', err);
      setError('Error al cargar los roles');
    }
  };

  // Si llegamos desde otra pantalla (ej. Programas) con un usuario a abrir, abrimos su edición
  // en cuanto la lista esté cargada -- mismo patrón que CatalogoEmpresas (highlightEntityId).
  useEffect(() => {
    if (!location.state?.highlightUserId || users.length === 0) return;
    const user = users.find((u) => String(u.id) === String(location.state.highlightUserId));
    if (user) handleEditClick(user);
    navigate(location.pathname + location.search, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, location.state]);

  const handleEditClick = (user) => {
    console.log('🔍 Usuario seleccionado para editar:', user);
    console.log('🔍 roleId del usuario:', user.roleId);
    console.log('🔍 role del usuario:', user.role);
    
    setSelectedUser(user);
    // Asegurarse de usar roleId en lugar de role
    const userToEdit = {
      ...user,
      passwordHash: '', // No mostrar hash
      roleId: user.roleId || user.role?.id || null
    };
    
    console.log('📝 Usuario preparado para editar:', userToEdit);
    console.log('📝 roleId final:', userToEdit.roleId);
    
    setEditUser(userToEdit);
    setIsEditMode(true);
    setDialogError(null); // Limpiar errores del diálogo
    setUserDialogOpen(true);
  };

  const handleCreateClick = () => {
    resetForm();
    setSelectedUser(null);
    setIsEditMode(false);
    setUserDialogOpen(true);
  };

  const handleDeleteClick = async (user) => {
    if (window.confirm(`¿Eliminar usuario ${user.username}?`)) {
      try {
        await api.delete(`/users/${user.id}`);
        fetchUsers();
      } catch (err) {
        setError('Error al eliminar el usuario');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setDialogError(null); // Limpiar errores previos del diálogo
      
      // Validaciones básicas antes de enviar
      if (!editUser.username || !editUser.email || !editUser.firstName || !editUser.firstLastName) {
        setDialogError('Todos los campos marcados con * son obligatorios');
        setLoading(false);
        return;
      }

      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editUser.email)) {
        setDialogError('Por favor ingresa un email válido');
        setLoading(false);
        return;
      }

      // Si no es modo edición, verificar si el usuario ya existe
      if (!isEditMode) {
        const existingUserCheck = await checkUserExists(editUser.username, editUser.email);
        if (existingUserCheck.exists) {
          setDialogError(existingUserCheck.message);
          setLoading(false);
          return;
        }
      }
      
      // Preparar datos para enviar al backend
      const userData = {
        ...editUser,
        roleId: editUser.roleId // Asegurar que se envía roleId
      };
      
      console.log('📤 Enviando datos de usuario:', userData);
      
      if (isEditMode) {
        await api.put(`/users/${selectedUser.id}`, userData);
      } else {
        await api.post('/users', userData);
      }
      setUserDialogOpen(false);
      fetchUsers();
      resetForm();
    } catch (err) {
      console.error('Error al guardar usuario:', err);
      console.error('Respuesta completa del error:', err.response);
      console.error('Datos del error:', err.response?.data);
      
      // Extraer mensaje de error de diferentes formatos posibles
      let errorMessage = '';
      if (err.response?.data) {
        errorMessage = err.response.data.message || 
                      err.response.data.error || 
                      err.response.data.details || 
                      err.response.data.description ||
                      JSON.stringify(err.response.data);
      }
      
      console.log('Mensaje de error extraído:', errorMessage);
      
      // Detectar patrones específicos en los logs del servidor
      const serverLogs = err.response?.data?.logs || '';
      const fullErrorContext = `${errorMessage} ${serverLogs}`.toLowerCase();
      
      console.log('Contexto completo del error:', fullErrorContext);
      
      // Manejar errores específicos del backend
      if (err.response?.status === 409 || err.response?.status === 400) {
        
        // Detectar error de email duplicado (incluyendo el patrón específico del log)
        if (errorMessage?.toLowerCase().includes('email') || 
            errorMessage?.toLowerCase().includes('correo') ||
            fullErrorContext.includes('email duplicado') ||
            fullErrorContext.includes(`email duplicado: ${editUser.email.toLowerCase()}`) ||
            (errorMessage?.toLowerCase().includes('duplicado') && editUser.email) ||
            (errorMessage && errorMessage.includes(editUser.email))) {
          setDialogError(`❌ El email "${editUser.email}" ya está registrado en el sistema.

👤 Este email ya pertenece a otro usuario en el sistema.

💡 Soluciones:
• Usa un email diferente
• Verifica si ya tienes una cuenta con este email
• Contacta al administrador si necesitas ayuda`);
        } 
        // Detectar error de username duplicado
        else if (errorMessage?.toLowerCase().includes('username') || 
                 errorMessage?.toLowerCase().includes('usuario') ||
                 (errorMessage?.toLowerCase().includes('duplicado') && editUser.username)) {
          setDialogError(`❌ El nombre de usuario "${editUser.username}" ya está registrado en el sistema.

👤 Este username ya pertenece a otro usuario en el sistema.

💡 Soluciones:
• Elige un nombre de usuario diferente
• Prueba agregando números o caracteres: ${editUser.username}123
• Usa variaciones como: ${editUser.username}_conem`);
        }
        // Error genérico de duplicado
        else if (errorMessage?.toLowerCase().includes('duplicate') || 
                 errorMessage?.toLowerCase().includes('duplicado') ||
                 errorMessage?.toLowerCase().includes('already exists') ||
                 errorMessage?.toLowerCase().includes('ya existe')) {
          setDialogError(`❌ Ya existe un usuario con estos datos en el sistema.

📋 Datos en conflicto:
• Username: ${editUser.username}
• Email: ${editUser.email}

💡 Por favor, verifica y cambia los datos que estén duplicados.`);
        }
        // Si hay mensaje pero no coincide con patrones conocidos
        else if (errorMessage && errorMessage !== 'undefined' && errorMessage.length > 0) {
          setDialogError(`❌ Error al crear el usuario: ${errorMessage}

💡 Por favor, revisa los datos e intenta nuevamente.`);
        }
        // Si no hay mensaje específico pero es un error 409/400 (conflicto/bad request)
        else {
          // Si detectamos que es específicamente un problema de email por el contexto
          if (fullErrorContext.includes(editUser.email.toLowerCase()) || 
              fullErrorContext.includes('duplicado')) {
            setDialogError(`❌ El email "${editUser.email}" ya está registrado en el sistema.

🔍 Se detectó un conflicto con este email en el servidor.

💡 Soluciones:
• Usa un email diferente
• Verifica si ya tienes una cuenta con este email
• Contacta al administrador si necesitas ayuda`);
          } else {
            setDialogError(`❌ No se pudo crear el usuario debido a datos duplicados.

📋 Verifica que no exista otro usuario con:
• Username: ${editUser.username}
• Email: ${editUser.email}

💡 Cambia estos datos e intenta nuevamente.`);
          }
        }
      } else if (err.response?.status === 422) {
        setDialogError(`❌ Los datos proporcionados no son válidos.

📋 Verifica:
• Todos los campos obligatorios estén llenos
• El formato del email sea correcto
• La contraseña cumpla los requisitos

💡 Corrige los errores e intenta nuevamente.`);
      } else if (err.response?.status === 500) {
        setDialogError(`❌ Error interno del servidor.

🔧 El sistema está experimentando problemas técnicos.

💡 Por favor, intenta nuevamente en unos momentos o contacta al administrador.`);
      } else {
        setDialogError(isEditMode 
          ? `❌ Error al actualizar el usuario.

🔧 No se pudieron guardar los cambios.

💡 Verifica tu conexión e intenta nuevamente.` 
          : `❌ Error al crear el usuario.

🔧 No se pudo completar el registro.

💡 Verifica todos los datos e intenta nuevamente.`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Función para verificar si un usuario ya existe
  const checkUserExists = async (username, email) => {
    console.log(`🔍 Verificando existencia de usuario: ${username} / ${email}`);
    
    try {
      // Verificar por username
      console.log(`🔍 Verificando username: ${username}`);
      try {
        const usernameCheck = await api.get(`/users/check-username/${username}`);
        console.log('✅ Respuesta verificación username:', usernameCheck.data);
        
        if (usernameCheck.data.exists) {
          const existingUser = usernameCheck.data.existingUser;
          return {
            exists: true,
            message: `❌ El nombre de usuario "${username}" ya está registrado en el sistema.

👤 Usuario existente: ${existingUser?.firstName || 'N/A'} ${existingUser?.firstLastName || 'N/A'}
📧 Email: ${existingUser?.email || 'N/A'}

💡 Soluciones:
• Elige un nombre de usuario diferente
• Prueba: ${username}123 o ${username}_conem
• Usa variaciones del nombre`
          };
        }
      } catch (usernameErr) {
        console.log('⚠️ Endpoint de verificación de username no disponible:', usernameErr.response?.status);
      }

      // Verificar por email
      console.log(`🔍 Verificando email: ${email}`);
      try {
        const emailCheck = await api.get(`/users/check-email/${email}`);
        console.log('✅ Respuesta verificación email:', emailCheck.data);
        
        if (emailCheck.data.exists) {
          const existingUser = emailCheck.data.existingUser;
          return {
            exists: true,
            message: `❌ El email "${email}" ya está registrado en el sistema.

👤 Usuario existente: ${existingUser?.firstName || 'N/A'} ${existingUser?.firstLastName || 'N/A'}
👨‍💻 Username: ${existingUser?.username || 'N/A'}

💡 Soluciones:
• Usa un email diferente
• Verifica si ya tienes cuenta con este email
• Contacta al administrador si es necesario`
          };
        }
      } catch (emailErr) {
        console.log('⚠️ Endpoint de verificación de email no disponible:', emailErr.response?.status);
      }

      console.log('✅ Usuario no existe, se puede crear');
      return { exists: false };
    } catch (err) {
      console.error('❌ Error general en verificación de usuario:', err);
      return { exists: false };
    }
  };

  // Nombre legible para el rol en el filtro -- Users.getRole() en el backend siempre devuelve
  // el valor en mayúsculas con guion bajo (ADMINISTRADOR, EJECUTIVO_OPERACIONES, etc.), así que
  // filtramos por ese valor exacto (no por el "name" de /roles, que puede venir con otra
  // capitalización) y solo lo formateamos para mostrarlo.
  const humanizeRole = (role) => (role || '')
    .toLowerCase()
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  const roleFilterOptions = [...new Set(users.map(u => u.role).filter(Boolean))].sort();

  // Filtrado por texto (usuario/correo/nombre completo) + rol + estado
  const filteredUsers = users.filter(user => {
    const term = filter.toLowerCase();
    const matchesSearch = !term ||
      user.username.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term) ||
      (user.firstName || '').toLowerCase().includes(term) ||
      (user.firstLastName || '').toLowerCase().includes(term) ||
      (user.secondLastName || '').toLowerCase().includes(term);
    const matchesRole = roleFilter === 'ALL' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'ALL' ||
      (statusFilter === 'ACTIVE' ? !!user.isActive : !user.isActive);
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Ordenamiento de la tabla (por columna, igual que en Catálogo de Empresas)
  const [sortField, setSortField] = useState('username');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSortRequest = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortValue = (user, field) => {
    if (field === 'company') {
      return user.role === 'INVITADO'
        ? (companyByEmail[(user.email || '').toLowerCase()] || '')
        : '';
    }
    return user[field] || '';
  };

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const valueA = getSortValue(a, sortField).toString().toLowerCase();
    const valueB = getSortValue(b, sortField).toString().toLowerCase();
    if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{
      width: '98%',
      maxWidth: '1800px',
      mx: 'auto',
      mt: 2,
      mb: 2,
      background: '#fff',
      borderRadius: 4,
      boxShadow: '0 4px 32px rgba(0,0,0,0.10)',
      p: 4,
    }}>
      {/* Header */}
      <Box sx={{ position: 'relative', mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            color: '#222',
            fontFamily: "'Fira Mono', 'Menlo', 'Monaco', 'Consolas', 'monospace'",
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::before': {
              content: '"👥"',
              marginRight: 2,
              fontSize: '1.2em'
            }
          }}
        >
          Catálogo de Usuarios
        </Typography>
        <Chip
          label={`Total: ${filteredUsers.length} usuarios`}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            backgroundColor: '#1976d2',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.9rem',
            '& .MuiChip-label': {
              px: 2,
              py: 1
            }
          }}
        />
      </Box>

      {/* Controls Section */}
      <Box sx={{ 
        mb: 4, 
        p: 3, 
        backgroundColor: '#f8f9fa', 
        borderRadius: 3, 
        border: '1px solid #e9ecef' 
      }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 3, 
            color: '#2c3e50', 
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '"🔍"',
              marginRight: 2,
              fontSize: '1.1em'
            }
          }}
        >
          Filtros y Acciones
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Buscar por usuario, correo o nombre"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              size="small"
              variant="outlined"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Rol</InputLabel>
              <Select
                value={roleFilter}
                label="Rol"
                onChange={e => setRoleFilter(e.target.value)}
              >
                <MenuItem value="ALL">Todos</MenuItem>
                {roleFilterOptions.map(role => (
                  <MenuItem key={role} value={role}>{humanizeRole(role)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                label="Estado"
                onChange={e => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">Todos</MenuItem>
                <MenuItem value="ACTIVE">Activo</MenuItem>
                <MenuItem value="INACTIVE">Inactivo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleCreateClick}
              startIcon={<PersonAddIcon />}
              sx={{
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Agregar Usuario
            </Button>
          </Grid>
        </Grid>
      </Box>
      {/* Users Table Section */}
      <Box sx={{ 
        mb: 4, 
        p: 3, 
        backgroundColor: '#f8f9fa', 
        borderRadius: 3, 
        border: '1px solid #e9ecef' 
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3 
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#2c3e50', 
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              '&::before': {
                content: '"📋"',
                marginRight: 2,
                fontSize: '1.1em'
              }
            }}
          >
            Lista de Usuarios
          </Typography>
          <IconButton 
            onClick={fetchUsers}
            sx={{ 
              bgcolor: 'primary.main',
              color: 'white',
              boxShadow: 2,
              '&:hover': {
                bgcolor: 'primary.dark',
                transform: 'rotate(180deg)',
                transition: 'all 0.3s ease-in-out'
              }
            }}
            title="Actualizar lista de usuarios"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
        
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        ) : (
          <TableContainer 
            component={Paper} 
            sx={{ 
              borderRadius: 2, 
              overflow: 'visible',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              width: '100%'
            }}
          >
            <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#fff', width: '12%' }}>
                    <TableSortLabel
                      active={sortField === 'username'}
                      direction={sortField === 'username' ? sortDirection : 'asc'}
                      onClick={() => handleSortRequest('username')}
                      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                    >
                      Usuario
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#fff', width: '22%' }}>
                    <TableSortLabel
                      active={sortField === 'email'}
                      direction={sortField === 'email' ? sortDirection : 'asc'}
                      onClick={() => handleSortRequest('email')}
                      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#fff', width: '10%' }}>
                    <TableSortLabel
                      active={sortField === 'firstName'}
                      direction={sortField === 'firstName' ? sortDirection : 'asc'}
                      onClick={() => handleSortRequest('firstName')}
                      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                    >
                      Nombre
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#fff', width: '8%' }}>
                    <TableSortLabel
                      active={sortField === 'firstLastName'}
                      direction={sortField === 'firstLastName' ? sortDirection : 'asc'}
                      onClick={() => handleSortRequest('firstLastName')}
                      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                    >
                      Apellido Paterno
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#fff', width: '8%' }}>
                    <TableSortLabel
                      active={sortField === 'secondLastName'}
                      direction={sortField === 'secondLastName' ? sortDirection : 'asc'}
                      onClick={() => handleSortRequest('secondLastName')}
                      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                    >
                      Apellido Materno
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#fff', width: '14%' }}>
                    <TableSortLabel
                      active={sortField === 'role'}
                      direction={sortField === 'role' ? sortDirection : 'asc'}
                      onClick={() => handleSortRequest('role')}
                      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                    >
                      Rol
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#fff', width: '10%' }}>
                    <TableSortLabel
                      active={sortField === 'company'}
                      direction={sortField === 'company' ? sortDirection : 'asc'}
                      onClick={() => handleSortRequest('company')}
                      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                    >
                      Empresa
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#fff', width: '8%' }} align="center">Estado</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#fff', width: '8%' }} align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No hay usuarios que coincidan con el filtro
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedUsers.map(user => (
                    <StyledTableRow key={user.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell sx={{ width: '12%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon sx={{ mr: 1, color: '#1976d2', fontSize: 20 }} />
                          <Typography variant="body2" fontWeight="medium" noWrap>
                            {user.username}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: '22%' }}>
                        <Typography variant="body2" color="textSecondary" noWrap title={user.email}>
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '10%' }}>
                        <Typography variant="body2" noWrap>
                          {user.firstName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '8%' }}>
                        <Typography variant="body2" noWrap>
                          {user.firstLastName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '8%' }}>
                        <Typography variant="body2" noWrap>
                          {user.secondLastName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '14%' }}>
                        <Chip
                          label={user.role}
                          color={
                            user.role === 'Administrador' ? 'error' :
                            user.role === 'Ventas' ? 'success' :
                            user.role === 'Coordinador de Operaciones' ? 'warning' :
                            user.role === 'Ejecutivo de Operaciones' ? 'info' :
                            user.role === 'Supervisor de Calidad' ? 'secondary' : 'primary'
                          }
                          size="small"
                          variant="outlined"
                          sx={{ maxWidth: '100%' }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '10%' }}>
                        {user.role === 'INVITADO' ? (
                          <Typography variant="body2" color="textSecondary" noWrap title={companyByEmail[(user.email || '').toLowerCase()] || ''}>
                            {companyByEmail[(user.email || '').toLowerCase()] || 'Sin empresa'}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="textSecondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ width: '8%' }}>
                        <Chip 
                          label={user.isActive ? 'Activo' : 'Inactivo'} 
                          color={user.isActive ? 'success' : 'default'} 
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ width: '8%', whiteSpace: 'nowrap' }}>
                        <IconButton 
                          onClick={() => handleEditClick(user)}
                          size="small"
                          sx={{ 
                            color: '#1976d2',
                            mr: 1,
                            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' } 
                          }}
                          title="Editar usuario"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDeleteClick(user)}
                          size="small"
                          sx={{ 
                            color: '#d32f2f', 
                            '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' } 
                          }}
                          title="Eliminar usuario"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      {/* Diálogo unificado para crear/editar usuario */}
      <Dialog
        open={userDialogOpen}
        onClose={() => { setUserDialogOpen(false); resetForm(); }}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e3f2fd 100%)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: '#fff',
          fontWeight: 700, 
          fontSize: '1.4rem',
          py: 3,
          textAlign: 'center',
          position: 'relative',
          '&::before': {
            content: isEditMode ? '"✏️"' : '"👤"',
            marginRight: 2,
            fontSize: '1.3em'
          }
        }}>
          {isEditMode ? 'Editar Usuario' : 'Agregar Nuevo Usuario'}
        </DialogTitle>
        <DialogContent sx={{
          p: 4,
          background: '#fff',
          borderRadius: '0 0 16px 16px',
          // Cajas de captura más grandes en todo el diálogo -- más alto y más relleno interno,
          // no depende del ancho de la ventana como sí depende maxWidth del Dialog.
          '& .MuiOutlinedInput-input, & .MuiSelect-select': {
            padding: '18px 16px',
            fontSize: '1.05rem',
          },
          '& .MuiInputLabel-root': {
            fontSize: '1.05rem',
          },
        }}>
          {dialogError && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                '& .MuiAlert-message': {
                  whiteSpace: 'pre-line',
                  fontSize: '0.95rem',
                  lineHeight: 1.5
                }
              }}
            >
              {dialogError}
            </Alert>
          )}
          
          {/* Sección de Información de Acceso */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                color: '#2c3e50', 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                pb: 1,
                borderBottom: '2px solid #e3f2fd',
                '&::before': {
                  content: '"🔐"',
                  marginRight: 2,
                  fontSize: '1.1em'
                }
              }}
            >
              Información de Acceso
            </Typography>
            <FormGrid container spacing={3}>
              <FormGrid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Usuario *"
                  value={editUser.username} 
                  onChange={handleUsernameChange}
                  margin="normal" 
                  variant="outlined"
                  required
                  error={!!fieldErrors.username}
                  helperText={fieldErrors.username || 'Mínimo 3 caracteres'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: fieldErrors.username ? '#f44336' : '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: fieldErrors.username ? '#f44336' : '#1976d2',
                      }
                    }
                  }}
                />
              </FormGrid>
              <FormGrid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Correo *"
                  type="email"
                  value={editUser.email} 
                  onChange={handleEmailChange}
                  margin="normal" 
                  variant="outlined"
                  required
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email || 'Formato: usuario@dominio.com'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: fieldErrors.email ? '#f44336' : '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: fieldErrors.email ? '#f44336' : '#1976d2',
                      }
                    }
                  }}
                />
              </FormGrid>
              <FormGrid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={isEditMode ? "Contraseña (dejar vacío para no cambiar)" : "Contraseña"}
                  type="password" 
                  value={editUser.passwordHash} 
                  onChange={e => setEditUser({ ...editUser, passwordHash: e.target.value })} 
                  margin="normal" 
                  variant="outlined"
                  required={!isEditMode}
                  helperText={isEditMode ? "Solo ingrese una nueva contraseña si desea cambiarla" : "Ingrese una contraseña segura para el nuevo usuario"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      }
                    }
                  }}
                />
              </FormGrid>
            </FormGrid>
          </Box>

          {/* Sección de Información Personal */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                color: '#2c3e50', 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                pb: 1,
                borderBottom: '2px solid #e3f2fd',
                '&::before': {
                  content: '"👤"',
                  marginRight: 2,
                  fontSize: '1.1em'
                }
              }}
            >
              Información Personal
            </Typography>
            <FormGrid container spacing={3}>
              <FormGrid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={editUser.firstName} 
                  onChange={e => setEditUser({ ...editUser, firstName: e.target.value })} 
                  margin="normal" 
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      }
                    }
                  }}
                  inputProps={{ maxLength: FIELD_LIMITS.personName }} />
              </FormGrid>
              <FormGrid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Apellido Paterno"
                  value={editUser.firstLastName} 
                  onChange={e => setEditUser({ ...editUser, firstLastName: e.target.value })} 
                  margin="normal" 
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      }
                    }
                  }}
                  inputProps={{ maxLength: FIELD_LIMITS.personName }} />
              </FormGrid>
              <FormGrid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Apellido Materno"
                  value={editUser.secondLastName} 
                  onChange={e => setEditUser({ ...editUser, secondLastName: e.target.value })} 
                  margin="normal" 
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      }
                    }
                  }}
                  inputProps={{ maxLength: FIELD_LIMITS.personName }} />
              </FormGrid>
            </FormGrid>
          </Box>

          {/* Sección de Configuración */}
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                color: '#2c3e50', 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                pb: 1,
                borderBottom: '2px solid #e3f2fd',
                '&::before': {
                  content: '"⚙️"',
                  marginRight: 2,
                  fontSize: '1.1em'
                }
              }}
            >
              Configuración de Cuenta
            </Typography>
            <FormGrid container spacing={3}>
              <FormGrid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      }
                    }
                  }}
                >
                  <InputLabel>Rol</InputLabel>
                  <Select
                    value={editUser.roleId || ''}
                    label="Rol"
                    onChange={e => {
                      console.log('🔄 Cambiando rol a:', e.target.value);
                      console.log('🔄 Estado actual editUser:', editUser);
                      setEditUser({ ...editUser, roleId: e.target.value });
                    }}
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FormGrid>
              <FormGrid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                      }
                    }
                  }}
                >
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={editUser.isActive}
                    label="Estado"
                    onChange={e => setEditUser({ ...editUser, isActive: e.target.value })}
                  >
                    <MenuItem value={true}>Activo</MenuItem>
                    <MenuItem value={false}>Inactivo</MenuItem>
                  </Select>
                </FormControl>
              </FormGrid>
            </FormGrid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          borderTop: '1px solid #e0e0e0'
        }}>
          <Button 
            onClick={() => { setUserDialogOpen(false); resetForm(); }}
            sx={{ 
              fontWeight: 600, 
              textTransform: 'none',
              px: 4,
              py: 1.5,
              borderRadius: 3,
              color: '#666',
              border: '2px solid #ddd',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#bbb'
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{ 
              fontWeight: 600, 
              textTransform: 'none',
              px: 4,
              py: 1.5,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              boxShadow: '0 4px 15px rgba(25, 118, 210, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                boxShadow: '0 6px 20px rgba(25, 118, 210, 0.6)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {isEditMode ? 'Guardar Cambios' : 'Crear Usuario'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CatalogoUsuarios;
